'use client';

import { ExternalLink } from 'lucide-react';

import { cn } from '../utils/cn';

export type PublicProfileBrandingDictionary = {
  publicProfile?: {
    branding?: {
      label?: string;
      linkAria?: string;
      tagline?: string;
    };
  };
};

const U2P_HOME_URL = 'https://www.up2promo.com';

type PublicProfileBrandingProps = {
  dictionary?: PublicProfileBrandingDictionary;
  className?: string;
  /** Tighter spacing for in-app phone preview. */
  compact?: boolean;
};

/** Official up2promo mark — brand colors stay fixed (not profile accent). */
function Up2promoLogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 1366 1366"
      aria-hidden
      className={cn('h-8 w-8 shrink-0', className)}
    >
      <polygon
        fill="#00CEFC"
        points="302.3,630.9 616.1,317 302.3,3 749.1,3 1063.1,317 1063.1,317 749.1,630.9"
      />
      <path
        fill="#2A7FFF"
        d="M1350.1,604v92.6c0,141.3-44.1,272.4-119,380.3c-22.6,32.8-48.3,63.6-76.5,91.7c-28.2,28-58.9,53.7-91.7,76.5
		C955.1,1320.1,824,1364,682.7,1364c-141.3,0-272.4-43.9-380.3-118.9c-65.5-45.5-122.6-102.7-168.2-168.2
		C61.7,972.6,18.2,846.6,15.5,710.7h-0.3v-79.8h287.1v65.7c0,210.1,170.3,380.3,380.3,380.3c104.9,0,200.1-42.5,269-111.3
		c68.7-68.8,111.2-163.9,111.2-269V316.7l0.3,0.3L1350.1,604z"
      />
    </svg>
  );
}

/**
 * “Powered by up2promo” — real logo + wordmark.
 * Uses profile design tokens (`--surface`, `--text`, …) so it reads correctly
 * on cards / centered / classic and light / dark.
 */
export function PublicProfileBranding({
  dictionary,
  className,
  compact = false,
}: PublicProfileBrandingProps) {
  const dict = dictionary?.publicProfile?.branding;
  const label = dict?.label ?? 'Powered by';
  const tagline = dict?.tagline ?? 'Digital business cards for modern teams';

  return (
    <div
      className={cn(
        'w-full shrink-0 px-4',
        compact ? 'pt-4 pb-2' : 'pt-7 pb-2',
        className,
      )}
    >
      <a
        href={U2P_HOME_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={dict?.linkAria ?? 'Visit up2promo website'}
        className={cn(
          'group mx-auto flex w-full max-w-2xl cursor-pointer flex-col items-center rounded-[16px] border border-[var(--border)] bg-[var(--surface)] text-center transition-[border-color,box-shadow,background-color] duration-200',
          'hover:border-[color-mix(in_oklab,var(--accent)_35%,var(--border))] hover:shadow-[0_10px_28px_-18px_var(--accent-16)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]',
          compact ? 'gap-2 px-4 py-3' : 'gap-2.5 px-5 py-4',
        )}
      >
        <span
          className={cn(
            'font-medium uppercase tracking-[0.7px] text-[var(--faint)]',
            compact ? 'text-[9.5px]' : 'text-[10px]',
          )}
        >
          {label}
        </span>

        <span
          className={cn(
            'flex items-center text-[var(--text)]',
            compact ? 'gap-2' : 'gap-2.5',
          )}
        >
          <Up2promoLogoMark className={compact ? 'h-6 w-6' : 'h-7 w-7'} />
          <span
            className={cn(
              'font-bold tracking-tight',
              compact ? 'text-[17px]' : 'text-[19px]',
            )}
          >
            up2promo
          </span>
          <ExternalLink
            className={cn(
              'text-[var(--faint)] transition-colors group-hover:text-[var(--accent)]',
              compact ? 'h-3.5 w-3.5' : 'h-4 w-4',
            )}
            aria-hidden
          />
        </span>

        <span
          className={cn(
            'text-[var(--muted)] transition-colors group-hover:text-[var(--text)]',
            compact ? 'text-[12px] leading-snug' : 'text-[13px] leading-snug',
          )}
        >
          {tagline}
        </span>
      </a>
    </div>
  );
}
